// Marketing-side schemas: cmsLegal disclaimer strings, cmsPages page blocks,
// public Inquiry submission. Phase 3 makes the CMS editor write to the
// underlying collections; for Phase 1 the seed values are the source of truth.

import { z } from 'zod';

// ── Legal / disclaimer strings ─────────────────────────────────────────
// Source-of-truth for the FCA framing copy. Must propagate to: public footer
// fca-strip, sign-up gate, request form, invoices, engagement letters.
// Edits must be made via CMS (Phase 3); never inline in component source.
export const CmsLegalSchema = z.object({
  fca_perimeter_notice: z.string(),
  invoice_descriptor: z.string(),
  contact_eligibility_body_corp: z.string(),
  contact_eligibility_sophisticated: z.string(),
  contact_eligibility_not_advice: z.string(),
  // Payment surfaces — Phase 5d reads these on the portal invoice page.
  // Optional until 5g (CMS disclaimer copy) ships official wording; client
  // falls back to baseline strings when unset.
  payment_descriptor: z.string().optional(),
  payment_failure_disclaimer: z.string().optional(),
  // Refund / cancellation policy — Phase 5f surfaces. Shown on the portal
  // invoice page and (future) engagement-letter / terms surface via
  // useLegal(). Optional so existing CmsLegal consumers and the GET fallback
  // stay valid. Content is DRAFT pending UK compliance solicitor sign-off
  // (REQ-013) — refund-entitlement terms carry consumer-rights + FCA-perimeter
  // implications, so they must not ship as final without review.
  // `refund_policy` is the full statement; `refund_policy_summary` is the
  // short line for the invoice footer.
  refund_policy: z.string().optional(),
  refund_policy_summary: z.string().optional(),
  // KYC / GDPR regulatory slots (portal-kyc Phase F). Top-level (NOT nested)
  // because the portal Compliance page reads them as top-level keys via
  // useLegal(). Optional so existing CmsLegal consumers and the GET fallback
  // stay valid. Content is DRAFT pending UK compliance solicitor sign-off
  // (REQ-013) — see cms.ts CMS_LEGAL for the source-of-truth values.
  kyc_processing_notice: z.string().optional(),
  kyc_consent_v1: z.string().optional(),
  data_retention_policy: z.string().optional(),
  cross_border_transfer_notice: z.string().optional(),
  // Cookie-consent surfaces — banner blurb + Preference Center category
  // descriptions. Optional so existing CmsLegal consumers and the GET
  // fallback stay valid. Source-of-truth per REQ-008 (the banner +
  // CookiePreferenceCenter read these via useLegal(), never hardcode).
  // Public-facing privacy copy — pending UK compliance solicitor sign-off
  // before production (REQ-013 spirit); components ship clean baseline
  // fallbacks so nothing breaks while CMS copy is authored.
  cookie_banner_blurb: z.string().optional(),
  cookie_cat_necessary: z.string().optional(),
  cookie_cat_analytics: z.string().optional(),
  cookie_cat_marketing: z.string().optional(),
  cookie_cat_functional: z.string().optional(),
  // Versioned for the material-change re-acceptance flag.
  version: z.string(),
  effective_at: z.string(), // ISO date
});
export type CmsLegal = z.infer<typeof CmsLegalSchema>;

// ── Inquiry submission (public contact form) ──────────────────────────
// All three eligibility checkboxes are z.literal(true) — the API rejects
// any submission where they're missing or false. This enforces brief
// constraint #1 (B2B-only gating) at the API level.
export const InquiryInputSchema = z.object({
  company: z.string().min(1, 'Company name is required'),
  country: z.enum(['UK', 'AE', 'EU', 'IN', 'SEA', 'NA', 'OT']),
  name: z.string().min(1, 'Authorised contact name is required'),
  email: z.string().email('Corporate email is required'),
  phone: z.string().optional(),
  service: z.string().min(1, 'Service is required'),
  brief: z.string().min(20, 'Brief must be at least 20 characters'),
  body_corporate_confirmed: z.literal(true, {
    errorMap: () => ({ message: 'Body-corporate confirmation is required.' }),
  }),
  sophisticated_self_cert: z.literal(true, {
    errorMap: () => ({ message: 'Sophisticated / HNW corporate self-certification is required.' }),
  }),
  not_advice_acknowledged: z.literal(true, {
    errorMap: () => ({ message: 'You must acknowledge Omega is not FCA-authorised.' }),
  }),
});
export type InquiryInput = z.infer<typeof InquiryInputSchema>;

export const InquiryOutputSchema = z.object({
  ok: z.literal(true),
  reference: z.string(),  // e.g. "OMG-2049"
});
export type InquiryOutput = z.infer<typeof InquiryOutputSchema>;
